import { QICM_DIMENSIONS, DIM_LEVELS, IIC_LEVELS, IFC_LEVELS, QICM_PROFILES, QICM_ITEM_FLAGS } from "@/data/qicm";

const L = (lang, fr, es) => (lang === "es" ? es : fr);

function dimLevel(score, feminine, lang) {
  const lvl = DIM_LEVELS.find((r) => score >= r.min && score <= r.max) || DIM_LEVELS[0];
  const g = lvl[lang];
  return { label: feminine ? g.f : g.m, color: lvl.color };
}
function iicLevel(iic, lang) {
  const r = IIC_LEVELS.find((x) => iic >= x.min && iic <= x.max) || IIC_LEVELS[2];
  return { label: r[lang], color: r.color };
}
function ifcLevel(ifc, lang) {
  if (ifc == null) return null;
  const r = IFC_LEVELS.find((x) => ifc >= x.min && ifc <= x.max) || IFC_LEVELS[0];
  return { label: r[lang], color: r.color };
}
function profileFor(reflexivite, certitude, lang) {
  const rHigh = reflexivite >= 21;
  const cHigh = certitude >= 21;
  let p = QICM_PROFILES.D;
  if (rHigh && !cHigh) p = QICM_PROFILES.A;
  else if (rHigh && cHigh) p = QICM_PROFILES.B;
  else if (!rHigh && cHigh) p = QICM_PROFILES.C;
  return { code: p.code, color: p.color, name: p.name[lang], axis: p.axis[lang], caracteristiques: p.caracteristiques[lang], noteLabel: p.noteLabel[lang], note: p.note[lang] };
}

export function computeQicmResults(answers, lang = "fr") {
  const scoreOf = (dim) => dim.items.reduce((s, n) => s + (Number.isFinite(answers[n]) ? answers[n] : 0), 0);
  const rows = QICM_DIMENSIONS.map((dim) => {
    const score = scoreOf(dim);
    return { key: dim.key, name: dim.name[lang], description: dim.description[lang], color: dim.color, score, level: dimLevel(score, dim.feminine, lang) };
  });
  const reflexivite = rows.find((r) => r.key === "reflexivite").score;
  const certitude = rows.find((r) => r.key === "certitude").score;
  const iic = reflexivite - certitude;
  const denom = reflexivite + certitude;
  const ifc = denom > 0 ? Math.round((reflexivite / denom) * 100) : null;
  const profile = profileFor(reflexivite, certitude, lang);
  const flaggedItems = Object.keys(QICM_ITEM_FLAGS).map(Number)
    .filter((n) => Number.isFinite(answers[n]) && answers[n] >= 3)
    .map((n) => ({ n, note: QICM_ITEM_FLAGS[n][lang], score: answers[n] }));
  return { rows, reflexivite, certitude, iic, iicLevel: iicLevel(iic, lang), ifc, ifcLevel: ifcLevel(ifc, lang), profile, flaggedItems };
}

export function qicmClinicalReading(results, lang = "fr") {
  const { reflexivite, certitude, iic, iicLevel, ifc, ifcLevel, profile } = results;
  const parts = [];
  parts.push(L(lang, `Le profil obtenu correspond au profil ${profile.code} « ${profile.name} » (${profile.axis}).`, `El perfil obtenido corresponde al perfil ${profile.code} «${profile.name}» (${profile.axis}).`));
  parts.push(L(lang, `L'auto-réflexivité est à ${reflexivite}/40 et la certitude cognitive à ${certitude}/40.`, `La autorreflexividad es de ${reflexivite}/40 y la certeza cognitiva de ${certitude}/40.`));
  parts.push(L(lang, `L'Indice d'Insight Cognitif (IIC = auto-réflexivité − certitude) est de ${iic > 0 ? "+" : ""}${iic}, soit un insight ${iicLevel.label.toLowerCase()}.`, `El Índice de Insight Cognitivo (IIC = autorreflexividad − certeza) es de ${iic > 0 ? "+" : ""}${iic}, es decir un insight ${iicLevel.label.toLowerCase()}.`));
  if (ifc != null && ifcLevel) {
    parts.push(L(lang, `L'Indice de Flexibilité Cognitive (IFC) est de ${ifc} %, indiquant une ${ifcLevel.label.toLowerCase()}.`, `El Índice de Flexibilidad Cognitiva (IFC) es de ${ifc} %, lo que indica una ${ifcLevel.label.toLowerCase()}.`));
  }
  return parts.join(" ");
}

export function qicmRecommendations(results, answers, lang = "fr") {
  const recs = [];
  if (results.reflexivite <= 20) recs.push(L(lang, "Développer la métacognition : questionnement socratique, journal des pensées, restructuration cognitive.", "Desarrollar la metacognición: cuestionamiento socrático, registro de pensamientos, reestructuración cognitiva."));
  if (results.certitude >= 21) recs.push(L(lang, "Assouplir les croyances : expériences comportementales, recherche d'hypothèses alternatives, vérification des preuves.", "Flexibilizar las creencias: experimentos conductuales, búsqueda de hipótesis alternativas, verificación de las pruebas."));
  if (Number.isFinite(answers[13]) && answers[13] >= 3) recs.push(L(lang, "Différencier émotion et réalité : défusion cognitive, pleine conscience, régulation émotionnelle.", "Diferenciar emoción y realidad: defusión cognitiva, mindfulness, regulación emocional."));
  if (Number.isFinite(answers[10]) && answers[10] <= 1) recs.push(L(lang, "Renforcer l'acceptation du doute : expositions graduées à l'incertitude, résolution de problèmes, thérapie ACT.", "Reforzar la aceptación de la duda: exposiciones graduales a la incertidumbre, resolución de problemas, terapia ACT."));
  return recs;
}
